(() => {
  'use strict';

  const SECTION_ID = 'cart-drawer-section';

  let modal = null;
  let currentProduct = null;

  /*
   * =========================================
   * HELPERS
   * =========================================
   */

  function getRootUrl() {
    return window.Shopify?.routes?.root || '/';
  }

  function getCartAddUrl() {
    return `${getRootUrl()}cart/add.js`;
  }

  function getSectionUrl() {
    const url = new URL(window.location.href);
    url.searchParams.set('sections', SECTION_ID);
    return url.toString();
  }

  function escapeHTML(value) {
    return String(value ?? '')
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
      .replaceAll("'", '&#039;');
  }

  function escapeAttribute(value) {
    return escapeHTML(value);
  }

  function formatMoney(cents) {
    if (window.Shopify?.formatMoney) {
      return window.Shopify.formatMoney(
        cents,
        window.theme?.moneyFormat || '${{amount}}'
      );
    }

    return `$${(Number(cents) / 100).toFixed(2)}`;
  }

  /*
   * Shopify product JSON can expose option data
   * in slightly different shapes.
   */
  function getOptionDisplayName(option, index) {
    if (typeof option === 'string') {
      return option;
    }

    if (option && typeof option === 'object') {
      return (
        option.name ||
        option.label ||
        option.title ||
        `Option ${index + 1}`
      );
    }

    return `Option ${index + 1}`;
  }

  function getOptionValueDisplay(value) {
    if (typeof value === 'string') {
      return value;
    }

    if (value && typeof value === 'object') {
      return (
        value.name ||
        value.label ||
        value.title ||
        String(value.value ?? '')
      );
    }

    return String(value ?? '');
  }

  /*
   * =========================================
   * MODAL
   * =========================================
   */

  function createModal() {
    if (modal) return modal;

    modal = document.createElement('dialog');
    modal.className = 'pick-your-3rd-modal';

    modal.setAttribute(
      'aria-labelledby',
      'pick-your-3rd-modal-title'
    );

    modal.innerHTML = `
      <div
        class="pick-your-3rd-modal__dialog"
        role="document"
      >
        <button
          type="button"
          class="pick-your-3rd-modal__close"
          data-upsell-modal-close
          aria-label="Close"
        >
          ×
        </button>

        <div data-upsell-modal-content></div>
      </div>
    `;

    document.body.appendChild(modal);

    modal.addEventListener('click', (event) => {
      if (event.target === modal) {
        destroyModal();
      }
    });

    modal.addEventListener('click', (event) => {
      const closeButton = event.target.closest(
        '[data-upsell-modal-close]'
      );

      if (closeButton) {
        destroyModal();
      }
    });

    modal.addEventListener('close', () => {
      document.documentElement.classList.remove(
        'pick-your-3rd-modal-open'
      );
    });

    return modal;
  }

  function openModal() {
    createModal();

    if (!modal.open) {
      modal.showModal();
    }

    document.documentElement.classList.add(
      'pick-your-3rd-modal-open'
    );

    requestAnimationFrame(() => {
      modal
        ?.querySelector('[data-upsell-modal-close]')
        ?.focus();
    });
  }

  function destroyModal() {
    if (!modal) return;

    if (modal.open) {
      modal.close();
    }

    modal.remove();

    modal = null;
    currentProduct = null;

    document.documentElement.classList.remove(
      'pick-your-3rd-modal-open'
    );
  }

  /*
   * =========================================
   * PRODUCT
   * =========================================
   */

  async function getProduct(handle) {
    const response = await fetch(
      `${getRootUrl()}products/${encodeURIComponent(handle)}.js`,
      {
        headers: {
          Accept: 'application/json'
        }
      }
    );

    if (!response.ok) {
      throw new Error('Unable to load product.');
    }

    return response.json();
  }

  /*
   * =========================================
   * SELECTED OPTIONS
   * =========================================
   */

  function getSelectedOptions() {
    if (!modal) return [];

    const inputs = Array.from(
      modal.querySelectorAll(
        '[data-upsell-option]:checked'
      )
    );

    const options = [];

    inputs.forEach((input) => {
      const index = Number(input.dataset.optionIndex);
      options[index] = input.value;
    });

    return options;
  }

  /*
   * =========================================
   * FIND VARIANT
   * =========================================
   */

  function findVariant(product) {
    if (!product?.variants?.length) {
      return null;
    }

    /*
     * Single default variant.
     */
    if (
      product.variants.length === 1 &&
      (
        !product.options ||
        getOptionDisplayName(product.options[0], 0) === 'Title'
      )
    ) {
      return product.variants[0];
    }

    const selectedOptions = getSelectedOptions();

    if (selectedOptions.length === 0) {
      return null;
    }

    return (
      product.variants.find((variant) => {
        if (!Array.isArray(variant.options)) {
          return false;
        }

        return variant.options.every(
          (option, index) =>
            String(getOptionValueDisplay(option)) ===
            String(selectedOptions[index] ?? '')
        );
      }) || null
    );
  }

  /*
   * =========================================
   * VARIANT STATE
   * =========================================
   */

  function updateVariantState() {
    if (!currentProduct || !modal) return;

    const variant = findVariant(currentProduct);

    const priceElement = modal.querySelector(
      '[data-upsell-modal-price]'
    );

    if (priceElement) {
      priceElement.textContent = formatMoney(
        variant
          ? variant.price
          : currentProduct.price
      );
    }

    const addButton = modal.querySelector(
      '[data-upsell-modal-add]'
    );

    if (!addButton) return;

    const hasRealOptions =
      Array.isArray(currentProduct.options) &&
      currentProduct.options.length > 0 &&
      getOptionDisplayName(
        currentProduct.options[0],
        0
      ) !== 'Title';

    if (hasRealOptions) {
      if (variant && variant.available) {
        addButton.disabled = false;
        addButton.textContent = 'ADD TO CART';
      } else {
        addButton.disabled = true;
        addButton.textContent = 'SELECT OPTIONS';
      }

      return;
    }

    const defaultVariant =
      currentProduct.variants?.[0];

    if (
      defaultVariant &&
      defaultVariant.available
    ) {
      addButton.disabled = false;
      addButton.textContent = 'ADD TO CART';
    } else {
      addButton.disabled = true;
      addButton.textContent = 'SOLD OUT';
    }
  }

  /*
   * =========================================
   * RENDER VARIANT MODAL
   * =========================================
   */

  function renderVariantModal(product) {
    createModal();

    currentProduct = product;

    const content = modal.querySelector(
      '[data-upsell-modal-content]'
    );

    if (!content) return;

    const image =
      product.featured_image ||
      product.images?.[0] ||
      '';

    const hasRealOptions =
      Array.isArray(product.options) &&
      product.options.length > 0 &&
      getOptionDisplayName(
        product.options[0],
        0
      ) !== 'Title';

    let optionsHTML = '';

    if (hasRealOptions) {
      optionsHTML = product.options
        .map((option, optionIndex) => {
          const optionName =
            getOptionDisplayName(
              option,
              optionIndex
            );

          const values = [
            ...new Set(
              (product.variants || [])
                .map((variant) => {
                  const rawValue =
                    variant.options?.[optionIndex];

                  return getOptionValueDisplay(
                    rawValue
                  );
                })
                .filter(Boolean)
            )
          ];

          return `
            <div class="pick-your-3rd-modal__option">

              <label
                class="pick-your-3rd-modal__option-label"
              >
                ${escapeHTML(optionName)}
              </label>

              <div class="pick-your-3rd-modal__values">

                ${values
                  .map((value, valueIndex) => {
                    const inputId =
                      `upsell-option-${optionIndex}-${valueIndex}`;

                    return `
                      <div class="pick-your-3rd-modal__value">

                        <input
                          type="radio"
                          id="${escapeAttribute(inputId)}"
                          name="upsell-option-${optionIndex}"
                          value="${escapeAttribute(value)}"
                          data-upsell-option
                          data-option-index="${optionIndex}"
                          ${
                            valueIndex === 0
                              ? 'checked'
                              : ''
                          }
                        >

                        <label
                          for="${escapeAttribute(inputId)}"
                        >
                          ${escapeHTML(value)}
                        </label>

                      </div>
                    `;
                  })
                  .join('')}

              </div>

            </div>
          `;
        })
        .join('');
    }

    content.innerHTML = `
      ${
        image
          ? `
            <img
              class="pick-your-3rd-modal__image"
              src="${escapeAttribute(image)}"
              alt="${escapeAttribute(product.title)}"
            >
          `
          : ''
      }

      <h2
        class="pick-your-3rd-modal__title"
        id="pick-your-3rd-modal-title"
      >
        ${escapeHTML(product.title)}
      </h2>

      <div
        class="pick-your-3rd-modal__price"
        data-upsell-modal-price
      >
        ${formatMoney(product.price)}
      </div>

      <div class="pick-your-3rd-modal__options">
        ${optionsHTML}
      </div>

      <button
        type="button"
        class="pick-your-3rd-modal__add"
        data-upsell-modal-add
      >
        ADD TO CART
      </button>

      <p
        class="pick-your-3rd-modal__error"
        data-upsell-modal-error
        hidden
      ></p>
    `;

    modal
      .querySelectorAll('[data-upsell-option]')
      .forEach((input) => {
        input.addEventListener(
          'change',
          updateVariantState
        );
      });

    openModal();
    updateVariantState();
  }

  /*
   * =========================================
   * ADD TO CART
   * =========================================
   */

  async function addVariantToCart(
    variantId,
    button
  ) {
    button.disabled = true;
    button.textContent = 'ADDING...';

    /*
     * Remove the variant popup immediately.
     */
    destroyModal();

    try {
      const response = await fetch(
        getCartAddUrl(),
        {
          method: 'POST',

          headers: {
            'Content-Type':
              'application/json',
            Accept:
              'application/json'
          },

          body: JSON.stringify({
            items: [
              {
                id: Number(variantId),
                quantity: 1
              }
            ],

            sections: SECTION_ID,
            sections_url:
              window.location.pathname
          })
        }
      );

      const data =
        await response.json();

      if (!response.ok) {
        throw new Error(
          data.description ||
          data.message ||
          'Unable to add this item.'
        );
      }

      if (
        data.sections &&
        data.sections[SECTION_ID]
      ) {
        updateCartDrawerSection(
          data.sections[SECTION_ID]
        );
      } else {
        await refreshCartDrawerSection();
      }

    } catch (error) {
      console.error(
        '[Pick Your 3rd Item]',
        error
      );
    }
  }

  /*
   * =========================================
   * UPDATE CART DRAWER
   * =========================================
   */

  function updateCartDrawerSection(html) {
    if (!html) return;

    const currentDrawer =
      document.querySelector(
        '#cart-drawer'
      );

    if (!currentDrawer) {
      console.warn(
        '[Pick Your 3rd Item] Current Cart Drawer not found.'
      );
      return;
    }

    const parser =
      new DOMParser();

    const parsedDocument =
      parser.parseFromString(
        html,
        'text/html'
      );

    const newDrawer =
      parsedDocument.querySelector(
        '#cart-drawer'
      );

    if (!newDrawer) {
      console.warn(
        '[Pick Your 3rd Item] New Cart Drawer not found.'
      );
      return;
    }

    const currentDialog =
      currentDrawer.querySelector(
        'dialog'
      );

    const newDialog =
      newDrawer.querySelector(
        'dialog'
      );

    if (!currentDialog || !newDialog) {
      console.warn(
        '[Pick Your 3rd Item] Cart Drawer dialog not found.'
      );
      return;
    }

    /*
     * Keep current <dialog> alive.
     * Only replace its contents.
     */
    currentDialog.innerHTML =
      newDialog.innerHTML;

    if (
      newDialog.hasAttribute(
        'cart-summary-sticky'
      )
    ) {
      currentDialog.setAttribute(
        'cart-summary-sticky',
        newDialog.getAttribute(
          'cart-summary-sticky'
        )
      );
    }

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        updateStickyState(
          currentDialog
        );
      });
    });
  }

  /*
   * =========================================
   * STICKY SUMMARY
   * =========================================
   */

  function updateStickyState(dialog) {
    if (!dialog) return;

    const content =
      dialog.querySelector(
        '.cart-drawer__content'
      );

    const summary =
      dialog.querySelector(
        '.cart-drawer__summary'
      );

    if (!content || !summary) {
      dialog.setAttribute(
        'cart-summary-sticky',
        'false'
      );
      return;
    }

    const drawerHeight =
      dialog.getBoundingClientRect()
        .height;

    const summaryHeight =
      summary.getBoundingClientRect()
        .height;

    if (!drawerHeight) return;

    const ratio =
      summaryHeight /
      drawerHeight;

    dialog.setAttribute(
      'cart-summary-sticky',
      ratio > 0.5
        ? 'false'
        : 'true'
    );
  }

  /*
   * =========================================
   * FALLBACK CART REFRESH
   * =========================================
   */

  async function refreshCartDrawerSection() {
    const response =
      await fetch(
        getSectionUrl(),
        {
          headers: {
            Accept:
              'application/json'
          }
        }
      );

    if (!response.ok) {
      throw new Error(
        'Unable to refresh cart drawer.'
      );
    }

    const data =
      await response.json();

    if (
      !data ||
      !data[SECTION_ID]
    ) {
      throw new Error(
        'Cart drawer section was not returned.'
      );
    }

    updateCartDrawerSection(
      data[SECTION_ID]
    );
  }

  /*
   * =========================================
   * EVENT DELEGATION
   * =========================================
   */

  document.addEventListener(
    'click',
    async (event) => {
      /*
       * Recommendation ADD
       */
      const addButton =
        event.target.closest(
          '[data-upsell-add]'
        );

      if (addButton) {
        event.preventDefault();

        const handle =
          addButton.dataset
            .productHandle;

        if (!handle) return;

        addButton.disabled = true;
        addButton.textContent =
          'LOADING...';

        try {
          const product =
            await getProduct(
              handle
            );

          renderVariantModal(
            product
          );

        } catch (error) {
          console.error(
            '[Pick Your 3rd Item]',
            error
          );

          addButton.disabled = false;
          addButton.textContent =
            'ADD';
        }

        return;
      }

      /*
       * Variant popup ADD
       */
      const modalAddButton =
        event.target.closest(
          '[data-upsell-modal-add]'
        );

      if (modalAddButton) {
        event.preventDefault();

        if (!currentProduct) {
          return;
        }

        const variant =
          findVariant(
            currentProduct
          );

        if (!variant) {
          const errorElement =
            modal?.querySelector(
              '[data-upsell-modal-error]'
            );

          if (errorElement) {
            errorElement.textContent =
              'Please select all options.';

            errorElement.hidden =
              false;
          }

          return;
        }

        if (!variant.available) {
          const errorElement =
            modal?.querySelector(
              '[data-upsell-modal-error]'
            );

          if (errorElement) {
            errorElement.textContent =
              'This variant is currently unavailable.';

            errorElement.hidden =
              false;
          }

          return;
        }

        await addVariantToCart(
          variant.id,
          modalAddButton
        );

        return;
      }
    }
  );

  /*
   * =========================================
   * ESC
   * =========================================
   */

  document.addEventListener(
    'keydown',
    (event) => {
      if (
        event.key === 'Escape' &&
        modal &&
        modal.open
      ) {
        destroyModal();
      }
    }
  );
})();